import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, X, Home, Search, Calendar, MessageCircle, User, LogOut, Shield, PawPrint, Heart, HelpCircle, Briefcase, ArrowLeftRight } from 'lucide-react';
import { getInitials } from '../lib/utils';
import NotificationBell from './NotificationBell';
import Logo from './Logo';

export const Navbar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Check if user has multiple roles (tutor + provider) - NOT for admin
    const hasMultipleRoles = useMemo(() => {
        if (!user || user.user_type === 'admin') return false;
        const roles = user.roles || [];
        const isTutor = roles.includes('tutor') || user.user_type === 'tutor' || user.user_type === 'client';
        const isProvider = roles.some(r => ['host', 'caregiver', 'driver', 'partner'].includes(r)) || 
                           ['host', 'caregiver', 'driver', 'partner', 'provider'].includes(user.user_type);
        return !!user.is_also_tutor || !!user.is_dual_profile || (isTutor && isProvider);
    }, [user]);

    const getActivePanel = useCallback(() => {
        if (!user) return null;
        const saved = localStorage.getItem('receppet_active_panel');
        if (saved && hasMultipleRoles) return saved;
        return user.user_type;
    }, [user, hasMultipleRoles]);

    const switchPanel = useCallback((panel) => {
        localStorage.setItem('receppet_active_panel', panel);
        navigate(panel === 'tutor' ? '/tutor' : '/provider');
    }, [navigate]);

    const getDashboardLink = useCallback(() => {
        if (!user) return '/login';
        if (user.user_type === 'admin') return '/admin';
        if (hasMultipleRoles) {
            const activePanel = getActivePanel();
            return activePanel === 'tutor' ? '/tutor' : '/provider';
        }
        switch (user.user_type) {
            case 'tutor':
            case 'client':
                return '/tutor';
            default:
                return '/provider';
        }
    }, [user, hasMultipleRoles, getActivePanel]);

    // Body scroll lock when mobile drawer is open
    useEffect(() => {
        if (drawerOpen) {
            const scrollY = window.scrollY;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [drawerOpen]);

    const navLinks = user ? [
        { href: '/services', label: 'Buscar Serviços', icon: Search },
        { href: '/bookings', label: 'Reservas', icon: Calendar },
        { href: '/chat', label: 'Mensagens', icon: MessageCircle },
    ] : [
        { href: '/services', label: 'Buscar Serviços', icon: Search },
    ];

    return (
        <nav className="sticky top-0 z-50 glass border-b border-slate-100" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center" data-testid="logo-link">
                        <Logo size="md" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-6">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={`nav-link flex items-center gap-2 ${location.pathname === link.href ? 'active' : ''}`}
                                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                            >
                                <link.icon className="w-4 h-4" />
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Right side */}
                    <div className="flex items-center gap-2">
                        {user && <NotificationBell />}
                        
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="user-menu-trigger">
                                        <Avatar className="w-9 h-9 border-2 border-[#4A90B2]">
                                            <AvatarImage src={user.profile_photo} />
                                            <AvatarFallback className="bg-[#4A90B2] text-white text-sm">
                                                {getInitials(user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="hidden sm:block font-medium text-sm text-slate-700">
                                            {user.name?.split(' ')[0]}
                                        </span>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56" portal={false}>
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium">{user.name}</p>
                                        <p className="text-xs text-slate-500">{user.email}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    
                                    {/* Panel Switcher - Only show if user has multiple roles */}
                                    {hasMultipleRoles && (
                                        <>
                                            <DropdownMenuLabel className="text-xs text-slate-400 font-normal">
                                                <ArrowLeftRight className="w-3 h-3 inline mr-1" />
                                                Trocar Painel
                                            </DropdownMenuLabel>
                                            <DropdownMenuItem 
                                                onClick={() => switchPanel('tutor')} 
                                                data-testid="switch-tutor-panel"
                                                className={getActivePanel() === 'tutor' ? 'bg-blue-50 text-blue-700' : ''}
                                            >
                                                <PawPrint className="w-4 h-4 mr-2" />
                                                Painel do Tutor
                                                {getActivePanel() === 'tutor' && <span className="ml-auto text-xs">✓</span>}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                                onClick={() => switchPanel('provider')} 
                                                data-testid="switch-provider-panel"
                                                className={getActivePanel() !== 'tutor' ? 'bg-[#4A90B2]/10 text-[#4A90B2]' : ''}
                                            >
                                                <Briefcase className="w-4 h-4 mr-2" />
                                                Painel do Prestador
                                                {getActivePanel() !== 'tutor' && <span className="ml-auto text-xs">✓</span>}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    
                                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                                        <User className="w-4 h-4 mr-2" />
                                        Meu Perfil
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                                        <Heart className="w-4 h-4 mr-2" />
                                        Favoritos
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/ajuda')} data-testid="menu-help">
                                        <HelpCircle className="w-4 h-4 mr-2" />
                                        Ajuda
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                                        <Home className="w-4 h-4 mr-2" />
                                        Painel
                                    </DropdownMenuItem>
                                    {user.user_type === 'admin' && (
                                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                                            <Shield className="w-4 h-4 mr-2" />
                                            Administração
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Sair
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/login')}
                                    className="text-slate-600 hover:text-[#4A90B2]"
                                    data-testid="login-btn"
                                >
                                    Entrar
                                </Button>
                                <Button
                                    onClick={() => navigate('/register')}
                                    className="btn-primary hidden sm:flex"
                                    data-testid="register-btn"
                                >
                                    Cadastrar
                                </Button>
                            </div>
                        )}

                        {/* Mobile menu button - available for ALL users */}
                        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                            <SheetTrigger asChild>
                                <button
                                    className="md:hidden p-2 rounded-lg hover:bg-slate-100"
                                    data-testid="mobile-menu-btn"
                                >
                                    <Menu className="w-6 h-6" />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72 bg-[#1e3a4c] border-l-0 p-0">
                                <div className="flex flex-col h-full">
                                    {/* Header */}
                                    <div className="p-4 border-b border-white/10">
                                        <div className="flex items-center gap-2">
                                            <Logo size="sm" />
                                        </div>
                                    </div>
                                    
                                    {/* User Info if logged */}
                                    {user && (
                                        <div className="p-4 border-b border-white/10">
                                            <div className="flex items-center gap-3">
                                                {user.profile_photo ? (
                                                    <img src={user.profile_photo} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-[#4A90B2] flex items-center justify-center text-white font-medium">
                                                        {user.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-white font-medium text-sm">{user.name}</p>
                                                    <p className="text-white/60 text-xs">{user.email}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Navigation */}
                                    <div className="flex-1 p-4 space-y-1 overflow-y-auto">
                                        {navLinks.map((link) => (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors ${
                                                    location.pathname === link.href ? 'bg-white/10 text-white' : ''
                                                }`}
                                                onClick={() => setDrawerOpen(false)}
                                            >
                                                <link.icon className="w-5 h-5" />
                                                {link.label}
                                            </Link>
                                        ))}
                                        
                                        {/* Help Link */}
                                        <Link
                                            to="/ajuda"
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                                            onClick={() => setDrawerOpen(false)}
                                        >
                                            <HelpCircle className="w-5 h-5" />
                                            Ajuda
                                        </Link>
                                        
                                        {user && (
                                            <>
                                                <Link
                                                    to="/favorites"
                                                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                                                    onClick={() => setDrawerOpen(false)}
                                                >
                                                    <Heart className="w-5 h-5" />
                                                    Favoritos
                                                </Link>
                                                <Link
                                                    to="/profile"
                                                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                                                    onClick={() => setDrawerOpen(false)}
                                                >
                                                    <User className="w-5 h-5" />
                                                    Meu Perfil
                                                </Link>
                                                <Link
                                                    to={getDashboardLink()}
                                                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                                                    onClick={() => setDrawerOpen(false)}
                                                >
                                                    <Home className="w-5 h-5" />
                                                    Meu Painel
                                                </Link>
                                                
                                                {/* Panel Switcher for users with multiple roles */}
                                                {hasMultipleRoles && (
                                                    <div className="mt-4 pt-4 border-t border-white/10">
                                                        <div className="flex items-center gap-2 px-4 mb-2 text-white/50 text-xs uppercase">
                                                            <ArrowLeftRight className="w-3 h-3" />
                                                            Trocar Painel
                                                        </div>
                                                        <div className="flex gap-2 px-4">
                                                            <button
                                                                onClick={() => { switchPanel('tutor'); setDrawerOpen(false); }}
                                                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                                                    getActivePanel() === 'tutor' 
                                                                        ? 'bg-[#4A90B2] text-white' 
                                                                        : 'bg-white/5 text-white/70 hover:bg-white/10'
                                                                }`}
                                                            >
                                                                <PawPrint className="w-4 h-4" />
                                                                Tutor
                                                            </button>
                                                            <button
                                                                onClick={() => { switchPanel('provider'); setDrawerOpen(false); }}
                                                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                                                    getActivePanel() !== 'tutor' 
                                                                        ? 'bg-[#4A90B2] text-white' 
                                                                        : 'bg-white/5 text-white/70 hover:bg-white/10'
                                                                }`}
                                                            >
                                                                <Briefcase className="w-4 h-4" />
                                                                Prestador
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    
                                    {/* Footer */}
                                    <div className="p-4 border-t border-white/10">
                                        {!user ? (
                                            <div className="space-y-2">
                                                <Button
                                                    onClick={() => { navigate('/login'); setDrawerOpen(false); }}
                                                    variant="outline"
                                                    className="w-full border-white/20 text-white hover:bg-white/10"
                                                >
                                                    Entrar
                                                </Button>
                                                <Button
                                                    onClick={() => { navigate('/register'); setDrawerOpen(false); }}
                                                    className="w-full bg-[#4A90B2] hover:bg-[#3d7a99] text-white"
                                                >
                                                    Cadastrar
                                                </Button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => { handleLogout(); setDrawerOpen(false); }}
                                                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                                            >
                                                <LogOut className="w-5 h-5" />
                                                Sair
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </nav>
    );
};
